import React, { useCallback, useState, useEffect } from "react";

import { useAuth } from "lib/firebase-provider/Auth";

import { useDataDates } from "lib/config";

import {
  useHistory,
  useLocation,
  useRouteMatch,
  Route,
} from "react-router-dom";

import { mapTypes } from "components/MobilityMap";

import GoogleSearchBox from "components/GoogleSearchBox";

import useDistancingArgs from "./useDistancingArgs";

import MapColorsLegend from "components/MobilityMap/MapColorLegend";

import { BASELINE_DATE } from "lib/config";
// import { countryExists, stateExists } from "lib/countries";

import { useCountryData, useStateData } from "lib/config";

import MapTypeSelector, { mapSelectorTypes } from "components/MapTypeSelector";

import DistancingMobilityMap from "./components/DistancingMobilityMap";
import DistancingTimeline from "./components/DistancingTimeline";

import { FormControl, Input, InputAdornment } from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";

import PrivateRoute from "lib/firebase-provider/PrivateRoute";


import TopMobilityRanking from "./components/DistancingMobilityRankings/TopMobilityRanking";
import BottomMobilityRanking from "./components/DistancingMobilityRankings/BottomMobilityRanking";

import DistancingHomeLayout from "./components/DistancingHomeLayout";
import { formatDateISO } from "lib/utils";
import { parseISO, subMonths, subWeeks } from "date-fns";

import { max } from "ramda";

function limitDate(d, min, max, defaultValue) {
  if (!d || d < min || d > max) {
    return defaultValue;
  } else {
    return d;
  }
}

const searchInputComponent = (
  <Input
    id="input-with-icon-adornment"
    placeholder="Buscar..."
    startAdornment={
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    }
  />
);

function SearchPlacesInput({ bounds, onPlacesChanged }) {
  return (
    <FormControl>
      <GoogleSearchBox
        bounds={bounds}
        onPlacesChanged={onPlacesChanged}
        inputComponent={searchInputComponent}
      />
    </FormControl>
  );
}

/**
 * This function returns the parameters (mapType and zoom information) of the map given the arguments.
 * @param {*} level
 * @param {*} hexagons
 * @param {*} country
 * @param {*} state
 * @param {*} selectedDate
 * @param {*} baselineDate
 */
function useMapParameters(
  level,
  hexagons,
  country,
  state,
  county,
  selectedDate,
  baselineDate
) {
  // export const mapTypes = {
  //   continentChoropleth: 1,
  //   countryChoropleth: 2,
  //   stateChoropleth: 3,
  //   countyChoropleth: 4,
  //   stateHexagons: 5,
  // };

  switch (level) {
    case "county":
      if (hexagons) {
        return {
          mapType: selectedDate && baselineDate && mapTypes.stateHexagons,
        };
      } else {
        return {
          mapType: selectedDate && baselineDate && mapTypes.countyChoropleth,
        };
      }
    case "state":
      if (hexagons) {
        return {
          mapType: selectedDate && baselineDate && mapTypes.stateHexagons,
        };
      } else {
        return {
          mapType: selectedDate && baselineDate && mapTypes.stateChoropleth,
        };
      }

    case "country":
      return {
        mapType: selectedDate && baselineDate && mapTypes.countryChoropleth,
      };
    case "world":
    default:
      return {
        mapType: selectedDate && baselineDate && mapTypes.worldChoropleth,
      };
  }
}

function renderMapSelector(
  level,
  mapSubType,
  onMapSubTypeChange,
  onHexResolutionChange,
  hexResolution
) {
  if (level === "state") {
    return (
      <MapTypeSelector
        onMapSubTypeChange={onMapSubTypeChange}
        mapSubType={mapSubType}
        onHexResolutionChange={onHexResolutionChange}
        hexResolution={hexResolution}
      />
    );
  } else {
    return null;
  }
}

function getPeriodFirstDate(period, maxDate) {
  switch (period) {
    case "week":
      return formatDateISO(subWeeks(parseISO(maxDate), 1));
    case "two-weeks":
      return formatDateISO(subWeeks(parseISO(maxDate), 2));
    case "month":
      return formatDateISO(subMonths(parseISO(maxDate), 1));
    default:
      return null;
  }
}

function Content({
  level,
  country,
  state,
  county,
  onCountryChange,
  onStateChange,
  onCountyChange,
  selectedDate,
  onSelectedDateChange,
  baselineDate,
  onBaselineDateChange,
  minDate,
  maxDate,
  period,
  onPeriodChange,
}) {
  const [mapSubType, setMapSubType] = useState(mapSelectorTypes.choropleth);
  const [bounds, setBounds] = useState(null);

  const [searchPlaces, setSearchPlaces] = useState(null);

  // TODO: Initial hexagon size is hard coded. Change it.
  const [hexResolution, setHexResolution] = useState(8);

  const mapTypeSelector = renderMapSelector(
    level,
    mapSubType,
    setMapSubType,
    setHexResolution,
    hexResolution
  );
  const [mapInstance, setMapInstance] = useState(null);

  const { mapType } = useMapParameters(
    level,
    mapSubType === mapSelectorTypes.hexagons && !!state,
    country,
    state,
    county,
    selectedDate,
    baselineDate
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getMapRef = useCallback((ref) => {
    if (ref) {
      setMapInstance(ref);
    }
  }, []);

  const countryData = useCountryData(country);


  const handlePolygonClick = useCallback(
    ({ polygonId }) => {
      if (level === "world") {
        onCountryChange(polygonId);
      } else if (level === "country") {
        countryData.availableLevels["level_2"] && onStateChange(polygonId);
      } else if (level === "state") {
        countryData.availableLevels["level_3"] && onCountyChange(polygonId);
      }
    },
    [onStateChange, onCountryChange, onCountyChange, countryData, level]
  );

  return (
    <DistancingHomeLayout
      minDate={minDate}
      maxDate={maxDate}
      country={country}
      selectedDate={selectedDate}
      onSelectedDateChange={onSelectedDateChange}
      baselineDate={baselineDate}
      onBaselineDateChange={onBaselineDateChange}
      period={period}
      onPeriodChange={onPeriodChange}
      search={
        <SearchPlacesInput bounds={bounds} onPlacesChanged={setSearchPlaces} />
      }
      mapTypeSelector={mapTypeSelector}
      onCountryChange={onCountryChange}
      colorReference={
        <div style={{ position: "absolute", top: 0, left: 0 }}>
          {mapInstance?.colorScale &&
            mapSubType === mapSelectorTypes.choropleth && (
              <MapColorsLegend colorScale={mapInstance.colorScale} />
            )}
        </div>
      }
      timeline={
        <DistancingTimeline
          minDate={minDate}
          maxDate={maxDate}
          level={level}
          country={country}
          state={state}
          selectedDate={selectedDate}
          baselineDate={baselineDate}
          onSelectDate={onSelectedDateChange}
        />
      }
      topRanking={
        <TopMobilityRanking
          level={level}
          country={country}
          state={state}
          selectedDate={selectedDate}
          baselineDate={baselineDate}
          onSelectDate={onSelectedDateChange}
        />
      }
      bottomRanking={
        <BottomMobilityRanking
          level={level}
          country={country}
          state={state}
          selectedDate={selectedDate}
          baselineDate={baselineDate}
          onSelectDate={onSelectedDateChange}
        />
      }
      map={
        <DistancingMobilityMap
          level={level}
          mapType={mapType}
          // {...mapPosition}
          ref={getMapRef}
          country={country}
          state={state}
          county={county}
          searchPlaces={searchPlaces}
          hexagons={mapSubType === mapSelectorTypes.hexagons && !!state}
          hexResolution={hexResolution}
          selectedDate={selectedDate}
          baselineDate={baselineDate}
          onPolygonClick={handlePolygonClick}
          onBoundsChange={setBounds}
        />
      }
    />
  );
}

function isAuthorized(currentUser, level) {
  return currentUser || level !== "state";
}

function useLimitedDate(startDate, endDate, defaultDate) {
  const [date, setDate] = useState(null);

  const limitedDate = limitDate(date, startDate, endDate, defaultDate);

  return [limitedDate, setDate];
}

// Component
function InnerRoute() {
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const { startDate, endDate } = useDataDates();

  const { level, country, state, county } = useDistancingArgs();

  const { getCurrentUser } = useAuth();

  const currentUser = getCurrentUser();

  const handleCountryChange = useCallback(
    (country) => {
      history.push(`${url}/${country}`);
    },
    [history, url]
  );
  const [period, setPeriod] = useState("all");

  const periodMinDate = max(startDate, getPeriodFirstDate(period, endDate));

  // TODO: Error page.
  const [selectedDate, setSelectedDate] = useLimitedDate(
    periodMinDate,
    endDate,
    endDate
  );

  const [baselineDate, setBaselineDate] = useLimitedDate(
    periodMinDate,
    endDate,
    max(BASELINE_DATE, periodMinDate)
  );

  const handleCountyChange = useCallback(
    (county) => {
      const countyUrl = `${url}/${country}/${state}/${county}`;
      if (isAuthorized(currentUser, "county")) {
        history.push(countyUrl);
      } else {
        history.push({
          pathname: "/login",
          state: { referrer: location, successUrl: countyUrl },
        });
      }
    },
    [history, url, country, state, location, currentUser]
  );

  const handleStateChange = useCallback(
    (state) => {
      const stateUrl = `${url}/${country}/${state}`;
      if (isAuthorized(currentUser, "state")) {
        history.push(stateUrl);
      } else {
        history.push({
          pathname: "/login",
          state: { referrer: location, successUrl: stateUrl },
        });
      }
    },
    [history, url, country, location, currentUser]
  );

  const countryData = useCountryData(country);
  const stateData = useStateData(country, state);

  const invalidCountry = country != null && !countryData;

  const invalidState = state != null && !stateData;
  const invalidGeography = invalidCountry || invalidState;


  useEffect(() => {
    if (invalidGeography) {
      history.push("/");
    }
  }, [invalidGeography, history]);

  return (
    <>
      {!invalidGeography && (
        <Content
          level={level}
          period={period}
          onPeriodChange={setPeriod}
          minDate={periodMinDate}
          maxDate={endDate}
          selectedDate={selectedDate}
          onSelectedDateChange={setSelectedDate}
          baselineDate={baselineDate}
          onBaselineDateChange={setBaselineDate}
          country={country}
          state={state}
          county={county}
          onCountryChange={handleCountryChange}
          onStateChange={handleStateChange}
          onCountyChange={handleCountyChange}
        />
      )}
    </>
  );
}
// Component.
export default function DistancingHomeMain(props) {
  // Current path: distancing/
  const { path } = useRouteMatch();

  return (
    <>
      <Route path={path}>
        <InnerRoute {...props} />
      </Route>

      <PrivateRoute exact path={`${path}/:country/:state`} />
      <PrivateRoute exact path={`${path}/:country/:state/:county`} />
    </>
  );
}
